import { Stack, Menu, MenuItem, Tooltip } from "@mui/material";
import { Button } from "@platform/design-system";
import AddIcon from "@mui/icons-material/Add";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router";

import { useBaselinesQuery } from "../../hooks/useBbe";
import { formatDate } from "../../utils";
import { useScenarioModelModal } from "../../hooks/useScenarioModelModal";

import { ScenarioModelModal } from "./components/ScenarioModelModal";
import { ScenarioModelsTable } from "./components/ScenarioModelsTable/ScenarioModelsTable";
import { SCENARIO_MODEL_MODE } from "./constants";
import { BBEOutletContext } from "./types";

export const ScenarioModeling: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const { brokerId, setSelectedModelId, selectedModelId } =
    useOutletContext<BBEOutletContext>();

  const menuOpen = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = (): void => {
    setAnchorEl(null);
  };

  const { data: baselinesData } = useBaselinesQuery(brokerId);
  const models = useMemo(
    () => baselinesData?.baselines ?? [],
    [baselinesData?.baselines],
  );

  const selectedModel = useMemo(
    () => models.find((m) => m.id === selectedModelId) ?? models[0] ?? null,
    [models, selectedModelId],
  );

  const formatModelLabel = (model: {
    label: string;
    createdAt: string;
  }): string => {
    const formattedCreatedAt = formatDate(model.createdAt, false, {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    return `${model.label} (${formattedCreatedAt?.replace(",", "") ?? ""})`;
  };

  useEffect(() => {
    const selectedExists = models.some((m) => m.id === selectedModelId);
    if ((!selectedModelId || !selectedExists) && models.length > 0) {
      setSelectedModelId(models[0].id);
    }
  }, [models, selectedModelId, setSelectedModelId]);

  const {
    onClickConfirm,
    handleOpenModal,
    handleExited,
    handleCloseModal,
    scenarioModelModalDefaultValueInput,
    modalOpen,
    modalMode,
    isSubmitting,
  } = useScenarioModelModal(selectedModel);

  return (
    <Stack direction="column" spacing={3}>
      <Stack direction="row" justifyContent="space-between">
        <Stack direction="row" spacing={1}>
          <Button
            endIcon={<ArrowDropDownIcon />}
            variant="tertiary"
            size="sm"
            aria-haspopup="true"
            aria-controls={menuOpen ? "scenario-modeling-menu" : undefined}
            aria-expanded={menuOpen ? "true" : false}
            onClick={handleClick}
          >
            {selectedModel
              ? formatModelLabel(selectedModel)
              : "Select Scenario Model"}
          </Button>
          <Menu
            id="scenario-modeling-menu"
            open={menuOpen}
            onClose={handleMenuClose}
            anchorEl={anchorEl}
          >
            {models?.map((model) => (
              <MenuItem
                key={model.id}
                onClick={() => {
                  setSelectedModelId(model.id);
                  handleMenuClose();
                }}
              >
                {formatModelLabel(model)}
              </MenuItem>
            ))}
          </Menu>

          <Button
            variant="tertiary"
            size="sm"
            onClick={() => handleOpenModal(SCENARIO_MODEL_MODE.RenameModelName)}
          >
            Rename Model
          </Button>
          <Tooltip
            title="A model is a set of scenarios with a baseline scenario as the data starting point."
            placement="top"
          >
            <Button
              variant="secondary"
              size="sm"
              onClick={() => handleOpenModal(SCENARIO_MODEL_MODE.Create)}
            >
              Create New Model
            </Button>
          </Tooltip>
        </Stack>

        <Tooltip
          title="A scenario is a hypothetical version with changes to show potential improvements."
          placement="top-end"
        >
          <Button
            variant="primary"
            size="sm"
            startIcon={<AddIcon />}
            onClick={() => handleOpenModal(SCENARIO_MODEL_MODE.Add)}
          >
            Add Scenario
          </Button>
        </Tooltip>
      </Stack>

      {selectedModel ? (
        <ScenarioModelsTable modelId={selectedModel.id} />
      ) : null}

      {modalMode !== null && (
        <ScenarioModelModal
          open={modalOpen}
          mode={modalMode}
          onClose={handleCloseModal}
          onExited={handleExited}
          defaultValueInput={scenarioModelModalDefaultValueInput}
          onConfirm={onClickConfirm}
          loading={isSubmitting}
        />
      )}
    </Stack>
  );
};

export default ScenarioModeling;
